import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { Text } from 'troika-three-text';
import { gsap } from 'gsap';
import GameMover from './GameMover.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

class DuckHunt {
    // --- Configuration ---
    config = {
        vr: {
            startPosition: { x: 0, y: 0, z: -1 },
            scale: 1,
        },
        ar: {
            moverStartPosition: new THREE.Vector3(0, -2, 0),
            moverStartScale: new THREE.Vector3(1, 0.1, 0.5),
        },
        ui: {
            buttonSize: 0.2,
            iconPaths: {
                play: 'assets/icons/play.png',
                restart: 'assets/icons/restart.png',
                passthroughShow: 'assets/icons/passthrough_show.png',
                passthroughHide: 'assets/icons/passthrough_hide.png',
                sfxOn: 'assets/icons/sfx_on.png',
                sfxOff: 'assets/icons/sfx_off.png',
            }
        },
        game: {
            bulletSpeed: 50,
            bulletTimeToLive: 0.5, // seconds
            duckSpeed: 3,
            roundDuration: 60, // seconds
            initialSpawnRate: 2500, // ms
            minSpawnRate: 500, // ms
            groundPigeonJumpChance: 0.005,
            groundPigeonJumpHeight: 1.8,
            jumpAnimationSpeed: 0.7,
            animationCrossfadeDuration: 0.2,
        }
    };

    // --- Game State ---
    gameEnded = true;
    score = 0;
    combo = 0;
    roundTime = 0;
    bullets = new THREE.Group();
    targets = [];
    blaster;
    mouse = new THREE.Vector2();
    desktopBlaster;
    mixers = []; // pigeon anims

    // --- Three.js Core ---
    camera;
    scene;
    renderer;
    gameContainer;

    ui2d;
    lastTime = 0;

    // --- XR & UI ---
    gameGroup;
    uiGroup;
    passtroughGroup;

    scoreText3d;
    timerText3d;
    comboText3d;
    mainActionButton;
    passthroughButton;
    sfxButton;
    controllers = [];
    raycaster;
    isPassthroughActive = false;
    isSfxMuted = false;
    textureLoader = new THREE.TextureLoader();
    gltfLoader = new GLTFLoader();
    audioListener;
    sounds = {};
    activeSounds = [];
    gameMover;
    duckSpawnTimeout = null;
    timerInterval = null;
    raycastPlane;
    pigeonModel = null;
    pigeonAnimations = [];

    muzzleFlashDesktop = null;
    muzzleFlashVR = null;

    muzzleLightDesktop = null;
    muzzleLightVR = null;


    constructor({ gameContainer, ui2d, leaderboard }) {
        this.gameContainer = gameContainer;
        this.leaderboard = leaderboard || null;
        this.ui2d = {
            container: ui2d,
            score: ui2d.querySelector('#score-2d'),
            timer: ui2d.querySelector('#timer-2d'),
            combo: ui2d.querySelector('#combo-2d'),
            start: ui2d.querySelector('#start-btn-2d'),
            restart: ui2d.querySelector('#restart-btn-2d'),
            sfxMute: ui2d.querySelector('#sfx-mute-btn-2d'),
            sfxIcon: ui2d.querySelector('#sfx-icon-2d'),


        };
        this.init();
        this.onResize();
    }

    init = () => {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        this.camera.position.set(0, 0, 0);

        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);

        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.shadowMap.enabled = true;
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.xr.enabled = true;
        this.renderer.setAnimationLoop(this.animation);
        this.gameContainer.appendChild(this.renderer.domElement);

        this.gameGroup = new THREE.Group();
        this.scene.add(this.gameGroup);

        this.passtroughGroup = new THREE.Group();
        this.scene.add(this.passtroughGroup);

        const ambientLight = new THREE.AmbientLight(0xababab, 0.6);
        this.scene.add(ambientLight);
        const dirLight = new THREE.DirectionalLight(0xfff4d6, 1.2);
        dirLight.castShadow = true;


        const fogColor = 0x2ab4f8ff;
        const density = 0.05;
        this.scene.fog = new THREE.FogExp2(fogColor, density);
        this.scene.background = new THREE.Color(fogColor);


        dirLight.shadow.mapSize.width = 2048;
        dirLight.shadow.mapSize.height = 2048;

        // Large frustum for full desktop coverage
        const desktopShadowSize = 20;
        dirLight.shadow.camera.top = desktopShadowSize;
        dirLight.shadow.camera.bottom = -desktopShadowSize;
        dirLight.shadow.camera.left = -desktopShadowSize;
        dirLight.shadow.camera.right = desktopShadowSize;

        dirLight.position.set(0, 5, 7.5);
        this.scene.add(dirLight);

        this.scene.add(this.bullets);
        this._createRaycastPlane();
        this.createSkyDome();
        this.bindDesktopControls();
        this.setupXrControls();
        this.create3dUi();
        this.create3dHud();
        this.loadAssets();
        this.createEnvironment();


        this.sun = this.createSun();
        const sunPosition = new THREE.Vector3();
        // We take the light's direction, reverse it, and place the sun far away along that vector.
        sunPosition.copy(dirLight.position).normalize().multiplyScalar(50);
        this.sun.position.copy(sunPosition);

        this.gameMover = new GameMover({
            scene: this.scene,
            gameGroup: this.gameGroup,
            controllers: this.controllers,
            camera: this.camera,
            moveSensitivity: 3,
            moverStartPosition: this.config.ar.moverStartPosition,
            moverStartScale: this.config.ar.moverStartScale
        });

        this.restart();

        gsap.ticker.lagSmoothing(false);
        gsap.ticker.remove(gsap.updateRoot);

        window.addEventListener('resize', this.onResize);
    }
    _createRaycastPlane = () => {
        this.raycastPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(100, 100),
            new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide })
        );
        this.raycastPlane.position.z = -5;
        this.scene.add(this.raycastPlane);
    }

    createSun = () => {
        const sunGeometry = new THREE.SphereGeometry(2, 32, 32);
        const sunMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffe0, // A slightly yellow-white color
            fog: false // --- This is the crucial part ---
        });
        const sun = new THREE.Mesh(sunGeometry, sunMaterial);

        // Set a high renderOrder to make sure the sun renders behind other transparent objects if any.

        this.passtroughGroup.add(sun); // Add it to the same group as the sky
        return sun;
    }

    createEnvironment = () => {
    }
    createSkyDome = () => {
        const skyGeometry = new THREE.SphereGeometry(500, 32, 32);

        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;

        const context = canvas.getContext('2d');
        const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#000033'); // Dark blue
        gradient.addColorStop(1, '#3cbbfaff'); // Light blue
        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);

        const skyTexture = new THREE.CanvasTexture(canvas);
        const skyMaterial = new THREE.MeshBasicMaterial({ map: skyTexture, side: THREE.BackSide, fog: true });

        const sky = new THREE.Mesh(skyGeometry, skyMaterial);
        this.passtroughGroup.add(sky);

    }

    createPigeon = (type = 'flying') => {
        if (!this.pigeonModel) return null;

        const pigeon = SkeletonUtils.clone(this.pigeonModel);
        pigeon.mixer = new THREE.AnimationMixer(pigeon);

        const initialAnimationName = type === 'flying' ? 'Fly' : 'Run';

        pigeon.userData = {
            type: type,
            state: type === 'flying' ? 'flying' : 'running',
            velocity: new THREE.Vector3(),
            mixer: pigeon.mixer,
            boundingSphere: new THREE.Sphere(new THREE.Vector3(), 0.6),
            collisionHelper: new THREE.Object3D(),
            jumpStartY: 0,
            jumpStartTime: 0,
            jumpDuration: 0,
            currentAction: null,
            // --- NEW BLEND SHAPE PROPERTIES ---
            skinnedMesh: null,
            deathBlendShapeIndex: -1,
        };

        pigeon.userData.collisionHelper.position.y = 40;
        pigeon.add(pigeon.userData.collisionHelper);

        // const debugSphere = new THREE.Mesh(
        //     new THREE.SphereGeometry(pigeon.userData.boundingSphere.radius + 20, 8, 8),
        //     new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true })
        // );
        // pigeon.userData.collisionHelper.add(debugSphere);

        // --- NEW: Find the mesh with blend shapes and the correct index ---
        pigeon.traverse(child => {
            if (child.isSkinnedMesh) {
                pigeon.userData.skinnedMesh = child;
                if (child.morphTargetInfluences) {
                    child.morphTargetInfluences.fill(0); // Reset on creation
                }
                if (child.morphTargetDictionary && child.morphTargetDictionary['eyes.dead'] !== undefined) {
                    pigeon.userData.deathBlendShapeIndex = child.morphTargetDictionary['eyes.dead'];
                }
            }
        });

        this.playAnimation(pigeon, initialAnimationName, true, 1.0, 0);

        this.mixers.push(pigeon.mixer);
        return pigeon;
    }


    spawnPigeon = () => {
        if (this.gameEnded) return;

        const pigeonType = Math.random() < 0.7 ? 'flying' : 'ground';
        const pigeon = this.createPigeon(pigeonType);
        if (!pigeon) return;

        const spawnSide = Math.random() < 0.5 ? 'left' : 'right';

        if (pigeon.userData.type === 'flying') {
            const randomHeight = Math.random() * 2 + 2;
            const randomZ = Math.random() * 2 - 8;
            if (spawnSide === 'left') {
                pigeon.position.set(-15, randomHeight, randomZ);
                pigeon.userData.velocity.x = this.config.game.duckSpeed;
                pigeon.rotation.y = Math.PI / 2;
            } else {
                pigeon.position.set(15, randomHeight, randomZ);
                pigeon.userData.velocity.x = -this.config.game.duckSpeed;
                pigeon.rotation.y = -Math.PI / 2;
            }
        } else { // Ground pigeon
            pigeon.position.y = -1.6;
            pigeon.position.z = -6;
            if (spawnSide === 'left') {
                pigeon.position.x = -15;
                pigeon.userData.velocity.x = this.config.game.duckSpeed * 1.8;
                pigeon.rotation.y = Math.PI / 2;
            } else {
                pigeon.position.x = 15;
                pigeon.userData.velocity.x = -this.config.game.duckSpeed * 1.8;
                pigeon.rotation.y = -Math.PI / 2;
            }
        }

        this.targets.push(pigeon);
        this.gameGroup.add(pigeon);
    }

    scheduleNextPigeonSpawn = () => {
        if (this.gameEnded) return;

        const { roundDuration, initialSpawnRate, minSpawnRate } = this.config.game;
        const progress = Math.max(0, this.roundTime / roundDuration);
        const spawnRate = minSpawnRate + (initialSpawnRate - minSpawnRate) * progress;

        this.duckSpawnTimeout = setTimeout(() => {
            this.spawnPigeon();
            this.scheduleNextPigeonSpawn();
        }, spawnRate);
    };


    animatePigeons = (deltaTime) => {
        for (let i = this.targets.length - 1; i >= 0; i--) {
            const pigeon = this.targets[i];

            switch (pigeon.userData.state) {
                case 'flying':
                    pigeon.position.addScaledVector(pigeon.userData.velocity, deltaTime);
                    if (Math.abs(pigeon.position.x) > 17) {
                        this.removePigeon(pigeon, i);
                    }
                    break;
                case 'running':
                    pigeon.position.addScaledVector(pigeon.userData.velocity, deltaTime);
                    if (Math.random() < this.config.game.groundPigeonJumpChance) {
                        this.triggerPigeonJump(pigeon);
                    }
                    if (Math.abs(pigeon.position.x) > 17) {
                        this.removePigeon(pigeon, i);
                    }
                    break;
                case 'jumping':
                    pigeon.position.addScaledVector(pigeon.userData.velocity, deltaTime);
                    const timeSinceJumpStart = (this.lastTime / 1000) - pigeon.userData.jumpStartTime;
                    const jumpProgress = Math.min(timeSinceJumpStart / pigeon.userData.jumpDuration, 1.0);
                    pigeon.position.y = pigeon.userData.jumpStartY + Math.sin(jumpProgress * Math.PI) * this.config.game.groundPigeonJumpHeight;

                    if (jumpProgress >= 1.0) {
                        pigeon.userData.state = 'running';
                        pigeon.position.y = pigeon.userData.jumpStartY;
                        this.playAnimation(pigeon, 'Run');
                    }
                    if (Math.abs(pigeon.position.x) > 17) {
                        this.removePigeon(pigeon, i);
                    }
                    break;
                case 'falling':
                    pigeon.position.y -= 5 * deltaTime;
                    if (pigeon.position.y < -1.6) {
                        pigeon.userData.state = 'dead';
                        setTimeout(() => this.removePigeon(pigeon, this.targets.indexOf(pigeon)), 1000);
                    }
                    break;
            }
        };
    }

    triggerPigeonJump = (pigeon) => {
        if (pigeon.userData.state !== 'running') return;

        pigeon.userData.state = 'jumping';
        const jumpClip = THREE.AnimationClip.findByName(this.pigeonAnimations, 'Jump');
        if (jumpClip) {
            const animationSpeed = this.config.game.jumpAnimationSpeed;
            pigeon.userData.jumpStartY = pigeon.position.y;
            pigeon.userData.jumpStartTime = this.lastTime / 1000;
            pigeon.userData.jumpDuration = jumpClip.duration / animationSpeed;

            this.playAnimation(pigeon, 'Jump', false, animationSpeed);
        }
    }

    playAnimation = (pigeon, animationName, loop = true, timeScale = 1.0, fadeDuration = this.config.game.animationCrossfadeDuration) => {
        const clip = THREE.AnimationClip.findByName(this.pigeonAnimations, animationName);
        if (!clip) {
            console.warn(`Animation "${animationName}" not found.`);
            return;
        }

        const newAction = pigeon.userData.mixer.clipAction(clip);
        const oldAction = pigeon.userData.currentAction;

        if (oldAction === newAction) {
            return;
        }

        newAction.reset();
        newAction.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce);
        if (!loop) newAction.clampWhenFinished = true;
        newAction.timeScale = timeScale;
        newAction.play();
        newAction.fadeIn(fadeDuration);

        if (oldAction) {
            oldAction.fadeOut(fadeDuration);
        }

        pigeon.userData.currentAction = newAction;
    }


    removePigeon = (pigeon, index) => {
        if (index > -1) {
            this.targets.splice(index, 1);
        }
        const mixerIndex = this.mixers.indexOf(pigeon.userData.mixer);
        if (mixerIndex > -1) {
            this.mixers.splice(mixerIndex, 1);
        }
        pigeon.removeFromParent();
    }


    async loadAssets() {
        const audioLoader = new THREE.AudioLoader();
        audioLoader.load('assets/duck_hunt/sounds/shotgun2.ogg', (buffer) => { this.sounds.laser = buffer; });
        audioLoader.load('assets/duck_hunt/sounds/pidgeon_hit.mp3', (buffer) => { this.sounds.score = buffer; });

        this.gltfLoader.load('assets/duck_hunt/shotgun/Shotgun.glb', (gltf) => {
            this.blaster = gltf.scene;
            this.desktopBlaster = SkeletonUtils.clone(this.blaster);

            this.blaster.scale.set(0.01, 0.01, 0.01);
            this.blaster.rotation.set(THREE.MathUtils.degToRad(-44.5), THREE.MathUtils.degToRad(-82.5), 0);

            this.scene.add(this.desktopBlaster);
            this.desktopBlaster.position.set(0, -0.7, -1);
            this.desktopBlaster.scale.set(0.03, 0.03, 0.03);

            this.desktopBlaster.visible = !this.renderer.xr.isPresenting;

            const muzzleTexture = this.textureLoader.load('assets/duck_hunt/shotgun/particles/muzzle.png');
            const muzzleMaterial = new THREE.SpriteMaterial({
                map: muzzleTexture,
                color: 0xff4500,
                transparent: true,
                blending: THREE.AdditiveBlending
            });


            //const muzzleMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 }); // A bright green
            //this.muzzleFlash = new THREE.Mesh(new THREE.BoxGeometry(5, 5, 5), muzzleMaterial);
            this.muzzleFlashDesktop = new THREE.Sprite(muzzleMaterial);
            this.muzzleFlashDesktop.scale.set(100, 100, 100);
            this.muzzleFlashDesktop.position.set(-43, 2, 0); // Adjust this to the tip of the blaster
            this.muzzleFlashDesktop.visible = false;
            if (this.desktopBlaster) {
                this.desktopBlaster.add(this.muzzleFlashDesktop);
            }
            this.muzzleFlashVR = new THREE.Sprite(muzzleMaterial);
            this.muzzleFlashVR.scale.set(25, 25, 25);
            this.muzzleFlashVR.position.set(-43, 2, 0); // Adjust this to the tip of the blaster
            this.muzzleFlashVR.visible = false;
            if (this.blaster) {
                this.blaster.add(this.muzzleFlashVR);
            }

            // Create and add the point lights
            this.muzzleLightDesktop = new THREE.PointLight(0xff4500, 0, 5);
            this.muzzleLightDesktop.position.copy(this.muzzleFlashDesktop.position);
            this.desktopBlaster.add(this.muzzleLightDesktop);

            this.muzzleLightVR = new THREE.PointLight(0xff4500, 0, 2);
            this.muzzleLightVR.position.copy(this.muzzleFlashVR.position);
            this.blaster.add(this.muzzleLightVR);

        });

        this.gltfLoader.load('assets/duck_hunt/Map_glb.glb', (gltf) => {
            const map = gltf.scene;
            const mapTexture = this.textureLoader.load('assets/duck_hunt/Standard.png');
            mapTexture.flipY = false;

            const mapMaterial = new THREE.MeshStandardMaterial({
                map: mapTexture,
                roughness: 1,
            });

            map.traverse((child) => {
                if (child.isMesh) {
                    child.material = mapMaterial;
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            });
            map.position.set(0, -1.6, 4);
            map.rotateY(Math.PI);
            this.passtroughGroup.add(map);
        });

        try {
            const [pigeonGltf, animationsGltf] = await Promise.all([
                this.gltfLoader.loadAsync('assets/duck_hunt/Pigeon_opt.glb'),
                this.gltfLoader.loadAsync('assets/duck_hunt/pigeonAnimations.glb')
            ]);

            this.pigeonModel = pigeonGltf.scene;
            this.pigeonAnimations = animationsGltf.animations;

            this.pigeonModel.scale.set(0.01, 0.01, 0.01);
            this.pigeonModel.traverse(child => {
                if (child.isMesh) {
                    child.castShadow = true;
                }
            });

        } catch (error) {
            console.error("Error loading pigeon assets:", error);
        }


    }

    onEnterXR = () => {

        if (this.desktopBlaster) this.desktopBlaster.visible = false;

        this.ui2d.container.style.display = 'none';
        const { x, y, z } = this.config.vr.startPosition;
        this.gameGroup.position.set(x, y, z);
        const vrScale = this.config.vr.scale;
        this.gameGroup.scale.set(vrScale, vrScale, vrScale);

        const session = this.renderer.xr.getSession();
        const passthroughSupported = session.environmentBlendMode === 'alpha-blend' || session.environmentBlendMode === 'additive';
        this.isPassthroughActive = true;
        this.togglePassthrough(true);

        this.passthroughButton.visible = passthroughSupported;
        this.sfxButton.visible = true;
        this.mainActionButton.visible = true;
        this.uiGroup.visible = true;
        this.setHudVisibility(true);
        if (this.gameMover) this.gameMover.activate();

        this.restart();
    }

    onExitXR = () => {

        if (this.desktopBlaster) this.desktopBlaster.visible = true;

        this.ui2d.container.style.display = 'flex';
        this.gameGroup.position.set(0, 0, 0);
        this.gameGroup.scale.set(1, 1, 1);

        this.passthroughButton.visible = false;
        this.sfxButton.visible = false;
        this.mainActionButton.visible = false;
        this.uiGroup.visible = false;
        this.setHudVisibility(false);

        this.scene.background = new THREE.Color(0x000000);

        this.camera.position.set(0, 0, 0);
        this.camera.lookAt(this.scene.position);
        if (this.gameMover) this.gameMover.deactivate();

        this.restart();
    }

    bindDesktopControls = () => {
        this.ui2d.start.addEventListener('click', this.start);
        this.ui2d.restart.addEventListener('click', this.start);
        this.gameContainer.addEventListener('mousedown', this.onDesktopAction);
        this.gameContainer.addEventListener('mousemove', this.onDesktopMouseMove);
        this.ui2d.sfxMute.addEventListener('click', this.toggleSfxMute);


    }
    onDesktopMouseMove = (event) => {
        const rect = this.gameContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        this.mouse.x = (x / rect.width) * 2 - 1;
        this.mouse.y = - (y / rect.height) * 2 + 1;
    }

    onDesktopAction = () => {
        if (this.renderer.xr.isPresenting) return;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = raycaster.intersectObject(this.raycastPlane);
        if (intersects.length > 0) {
            this.shoot(null, intersects[0].point);
        }

    }

    setupXrControls = () => {
        this.raycaster = new THREE.Raycaster();
        for (let i = 0; i < 2; i++) {
            const controller = this.renderer.xr.getController(i);
            controller.addEventListener('selectstart', () => this.handleControllerSelect(controller));
            controller.addEventListener('connected', (event) => {
                if (event.data.handedness === 'right' && this.blaster) {
                    const grip = this.renderer.xr.getControllerGrip(i);
                    grip.add(this.blaster);
                }
            });

            const pointerGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)]);
            const pointer = new THREE.Line(pointerGeometry);
            pointer.name = 'pointer';
            pointer.scale.z = 5;
            controller.add(pointer);

            this.scene.add(controller);
            const grip = this.renderer.xr.getControllerGrip(i);
            this.scene.add(grip);
            this.controllers.push(controller);
        }
    }



    handleControllerSelect = (controller) => {
        const intersections = this.getIntersections(controller);
        if (intersections.length > 0) {
            const button = intersections[0].object.parent;
            this.animateButtonClick(button);
            if (button === this.mainActionButton && this.gameEnded) {
                this.start();
            } else if (button === this.passthroughButton) {
                this.togglePassthrough();
            } else if (button === this.sfxButton) {
                this.toggleSfxMute();
            }
        } else if (!this.gameEnded) {
            this.shoot(controller);
        }
    }

    create3dUi = () => {
        this.uiGroup = new THREE.Group();
        this.uiGroup.position.set(0, -1, -1);
        this.gameGroup.add(this.uiGroup);

        this.mainActionButton = this.createUiButton(this.config.ui.iconPaths.play, 0.4);
        this.uiGroup.add(this.mainActionButton);

        this.passthroughButton = this.createUiButton(this.config.ui.iconPaths.passthroughShow);
        this.passthroughButton.position.set(0.4, 0, 0);
        this.uiGroup.add(this.passthroughButton);

        this.sfxButton = this.createUiButton(this.config.ui.iconPaths.sfxOn);
        this.sfxButton.position.set(-0.4, 0, 0);
        this.uiGroup.add(this.sfxButton);

        this.uiGroup.visible = false;
    }

    create3dHud = () => {
        const createText = (text, anchorX, positionX) => {
            const textMesh = new Text();
            textMesh.text = text;
            textMesh.fontSize = 0.08;
            textMesh.color = 0xFFFFFF;
            textMesh.anchorX = anchorX;
            textMesh.position.set(positionX, -0.5, -1);
            textMesh.visible = false;
            textMesh.sync();
            this.gameGroup.add(textMesh);
            return textMesh;
        };

        this.timerText3d = createText('Time: 60', 'right', -0.4);
        this.scoreText3d = createText('Score: 0', 'center', 0);
        this.comboText3d = createText('Combo: 0', 'left', 0.4);
    }


    setHudVisibility = (visible) => {
        const is3dVisibile = visible && this.renderer.xr.isPresenting;
        if (this.timerText3d) {
            this.timerText3d.visible = is3dVisibile;
        }
        if (this.scoreText3d) this.scoreText3d.visible = is3dVisibile;
        if (this.comboText3d) this.comboText3d.visible = is3dVisibile;

        const display = visible ? 'block' : 'none';
        this.ui2d.timer.style.display = display;
        this.ui2d.score.style.display = display;
        this.ui2d.combo.style.display = display;
    }

    setPointerVisibility = (visible) => {
        this.controllers.forEach(controller => {
            const pointer = controller.getObjectByName('pointer');
            if (pointer) {
                pointer.visible = visible;
            }
        });
    }

    createUiButton = (iconPath, size = this.config.ui.buttonSize) => {
        const button = new THREE.Group();
        const background = new THREE.Mesh(
            new THREE.CircleGeometry(size / 2, 32),
            new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5 })
        );
        button.add(background);

        const iconMaterial = new THREE.MeshBasicMaterial({ map: this.textureLoader.load(iconPath), transparent: true });
        const icon = new THREE.Mesh(new THREE.PlaneGeometry(size * 0.7, size * 0.7), iconMaterial);
        icon.position.z = 0.01;
        button.add(icon);
        button.userData.iconMaterial = iconMaterial;
        return button;
    }

    animateButtonClick = (button) => {
        gsap.timeline()
            .to(button.scale, { x: 0.8, y: 0.8, z: 0.8, duration: 0.1 })
            .to(button.scale, { x: 1, y: 1, z: 1, duration: 0.1 });
    }

    togglePassthrough = (forceState) => {
        this.isPassthroughActive = forceState !== undefined ? forceState : !this.isPassthroughActive;
        this.scene.background = this.isPassthroughActive ? null : new THREE.Color(0x1a1a1a);
        const iconPath = this.isPassthroughActive ? this.config.ui.iconPaths.passthroughHide : this.config.ui.iconPaths.passthroughShow;
        this.passthroughButton.userData.iconMaterial.map = this.textureLoader.load(iconPath);
        this.passtroughGroup.visible = this.isPassthroughActive;
    }

    toggleSfxMute = () => {
        this.isSfxMuted = !this.isSfxMuted;
        const newIconPath = this.isSfxMuted ? this.config.ui.iconPaths.sfxOff : this.config.ui.iconPaths.sfxOn;
        this.sfxButton.userData.iconMaterial.map = this.textureLoader.load(newIconPath);
        this.ui2d.sfxIcon.src = newIconPath;

    }

    getIntersections = (controller) => {
        this.raycaster.setFromXRController(controller);
        return this.raycaster.intersectObjects(this.uiGroup.children, true);
    }

    start = () => {
        this.restart();
        this.gameEnded = false;

        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = false;
            this.setHudVisibility(true);
            this.setPointerVisibility(false);

        } else {
            this.ui2d.start.style.display = 'none';
            this.ui2d.restart.style.display = 'none';
            this.setHudVisibility(true);
        }

        this.timerInterval = setInterval(this.updateTimer, 1000);
        this.scheduleNextPigeonSpawn();
    }

    endGame = () => {
        this.gameEnded = true;
        if (this.timerInterval) clearInterval(this.timerInterval);
        if (this.duckSpawnTimeout) clearTimeout(this.duckSpawnTimeout);
        this.timerInterval = null;
        this.duckSpawnTimeout = null;
        this.setPointerVisibility(true);
        const finalScore = this.score || 0;
        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = true;
            this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(this.config.ui.iconPaths.restart);
            if (this.leaderboard) this.leaderboard.submit(finalScore);
        } else {
            if (this.leaderboard && finalScore > 0) {
                this.leaderboard.submitAndShow(finalScore, () => {
                    this.ui2d.restart.click();
                });
            } else {
                this.ui2d.restart.style.display = 'block';
            }
        }
    }

    restart = () => {
        this.endGame();
        this.gameEnded = true;

        this.updateScore(0);
        this.updateCombo(0);
        this.updateTimerDisplay(this.config.game.roundDuration);


        this.bullets.children.forEach(b => b.removeFromParent());
        for (let i = this.targets.length - 1; i >= 0; i--) {
            this.removePigeon(this.targets[i], i);
        }

        this.targets = [];

        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = true;
            this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(this.config.ui.iconPaths.play);
            this.setHudVisibility(false);
        } else {
            this.ui2d.start.style.display = 'block';
            this.ui2d.restart.style.display = 'none';
            this.setHudVisibility(false);

        }
    }

    shoot = (controller, targetPosition) => {
        this.playSound('laser');


        const muzzleFlash = controller ? this.muzzleFlashVR : this.muzzleFlashDesktop;
        const muzzleLight = controller ? this.muzzleLightVR : this.muzzleLightDesktop;

        if (muzzleFlash) {
            muzzleFlash.visible = true;
            muzzleFlash.material.rotation = Math.random() * Math.PI * 2;
            gsap.fromTo(muzzleFlash.scale, { x: 0, y: 0, z: 0 }, {
                x: 25, y: 25, z: 25, duration: 0.1, onComplete: () => {
                    muzzleFlash.visible = false;
                }
            });
        }
        if (muzzleLight) {
            muzzleLight.intensity = 2;
            gsap.to(muzzleLight, { intensity: 0, duration: 0.1 });
        }

        const bullet = new THREE.Mesh(
            new THREE.SphereGeometry(0.05, 8, 8),
            new THREE.MeshBasicMaterial({ color: 0xff0000 })
        );

        let direction = new THREE.Vector3();
        let startPosition = new THREE.Vector3();

        if (controller) {
            const controllerToUse = controller;
            controllerToUse.getWorldPosition(startPosition);
            direction = new THREE.Vector3(0, 0, -1).applyQuaternion(controllerToUse.quaternion);
        } else {
            this.desktopBlaster.getWorldPosition(startPosition);
            direction.subVectors(targetPosition, startPosition).normalize();
        }

        bullet.position.copy(startPosition);
        bullet.userData.velocity = direction.multiplyScalar(this.config.game.bulletSpeed);
        bullet.userData.timeToLive = this.config.game.bulletTimeToLive;
        bullet.userData.hit = false;

        this.bullets.add(bullet);
    }

    playSound = (name) => {
        if (this.isSfxMuted || !this.sounds[name]) return;
        const sound = new THREE.Audio(this.audioListener);
        sound.setBuffer(this.sounds[name]);
        this.activeSounds.push(sound);
        sound.onEnded = () => {
            this.activeSounds = this.activeSounds.filter((activeSound) => activeSound !== sound);
        };
        sound.play();
    }

    updateScore = (newScore) => {
        this.score = newScore;
        this.ui2d.score.textContent = `Score: ${this.score}`;
        if (this.scoreText3d) {
            this.scoreText3d.text = `Score: ${this.score}`;
            this.scoreText3d.sync();
        }
    }

    updateCombo = (newCombo) => {
        this.combo = newCombo;
        this.ui2d.combo.textContent = `Combo: ${this.combo}`;
        if (this.comboText3d) {
            this.comboText3d.text = `Combo: ${this.combo}`;
            this.comboText3d.sync();
        }
    }

    updateTimer = () => {
        this.roundTime--;
        this.updateTimerDisplay(this.roundTime);
        if (this.roundTime <= 0) {
            this.endGame();
        }
    }

    updateTimerDisplay = (time) => {
        this.roundTime = time;
        this.ui2d.timer.textContent = `Time: ${time}`;
        if (this.timerText3d) {
            this.timerText3d.text = `Time: ${time}`;
            this.timerText3d.sync();
        }
    }

    showFloatingText = (text, position) => {
        const floatingText = new Text();
        floatingText.text = text;
        floatingText.fontSize = 0.5;
        floatingText.color = 0xFFFFFF;
        floatingText.outlineWidth = '1%'; // Add outline
        floatingText.outlineColor = 0x000000;
        floatingText.anchorX = 'center';
        floatingText.anchorY = 'middle';
        floatingText.position.copy(position);
        floatingText.material.transparent = true;

        this.gameGroup.add(floatingText);
        floatingText.sync(() => {
            gsap.to(floatingText.position, { y: floatingText.position.y + 1, duration: 1.5 });
            gsap.to(floatingText, {
                fillOpacity: 0,
                outlineOpacity: 0,
                duration: 1.5,
                onComplete: () => {
                    this.gameGroup.remove(floatingText);
                    floatingText.dispose();
                }
            });
        });
    }
    animation = (time, frame) => {
        const deltaTime = (time - this.lastTime) / 1000;
        this.lastTime = time; // time is in milliseconds
        gsap.updateRoot(time / 1000);

        if (this.gameMover) this.gameMover.update();

        // Update all animation mixers
        for (const mixer of this.mixers) {
            mixer.update(deltaTime);
        }

        if (!this.renderer.xr.isPresenting && this.desktopBlaster) {
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(this.mouse, this.camera);
            const intersects = raycaster.intersectObject(this.raycastPlane);
            if (intersects.length > 0) {
                const targetPoint = intersects[0].point;
                this.desktopBlaster.lookAt(targetPoint);
                this.desktopBlaster.rotateY(THREE.MathUtils.degToRad(90));
            }
        }
        if (!this.gameEnded) {
            this.animatePigeons(deltaTime);

            for (let i = this.bullets.children.length - 1; i >= 0; i--) {
                const bullet = this.bullets.children[i];
                bullet.position.addScaledVector(bullet.userData.velocity, deltaTime);
                bullet.userData.timeToLive -= deltaTime;
                if (bullet.userData.timeToLive <= 0) {
                    if (!bullet.userData.hit) {
                        this.updateCombo(0);
                    }
                    bullet.removeFromParent();
                }
            }

            this.targets.forEach(target => {
                if (target.userData.state === 'falling' || target.userData.state === 'dead') return;
                target.userData.collisionHelper.getWorldPosition(target.userData.boundingSphere.center);

                for (let i = this.bullets.children.length - 1; i >= 0; i--) {
                    const bullet = this.bullets.children[i];
                    if (bullet.userData.hit) continue;

                    if (target.userData.boundingSphere.containsPoint(bullet.position)) {
                        target.userData.state = 'falling';
                        bullet.userData.hit = true;
                        bullet.removeFromParent();

                        this.playSound('score');
                        this.updateCombo(this.combo + 1);

                        if (target.userData.type === 'ground') {
                            this.updateTimerDisplay(this.roundTime + 5);
                            this.showFloatingText('+5s', target.position.clone().add(new THREE.Vector3(0, 0.5, 0)));
                        }
                        else if (target.userData.type === 'flying') {
                            const baseScore = 10;
                            const comboMultiplier = 10;
                            // The combo is already updated, so combo of 1 is the first hit
                            const scoreGained = baseScore + (comboMultiplier * (this.combo - 1));

                            this.updateScore(this.score + scoreGained);
                            this.showFloatingText(`+${scoreGained}`, target.position);
                        }


                        this.playAnimation(target, 'Death', false);

                        // --- NEW: Set the blend shape on death ---
                        if (target.userData.skinnedMesh && target.userData.deathBlendShapeIndex !== -1) {
                            target.userData.skinnedMesh.morphTargetInfluences[target.userData.deathBlendShapeIndex] = 1.0;
                        }

                        break;
                    }
                }
            });
        }

        this.renderer.render(this.scene, this.camera);
    }

    onResize = () => {
        const { clientWidth, clientHeight } = this.gameContainer;
        this.renderer.setSize(clientWidth, clientHeight);
        this.camera.aspect = clientWidth / clientHeight;
        this.camera.updateProjectionMatrix();
    }

    dispose = () => {
        this.renderer.setAnimationLoop(null);
        window.removeEventListener('resize', this.onResize);
        this.gameContainer.removeEventListener('mousedown', this.onDesktopAction);
        this.gameContainer.removeEventListener('mousemove', this.onDesktopMouseMove);

        if (this.timerInterval) clearInterval(this.timerInterval);
        if (this.duckSpawnTimeout) clearTimeout(this.duckSpawnTimeout);

        this.activeSounds.forEach((sound) => {
            if (sound.isPlaying) sound.stop();
            if (typeof sound.disconnect === 'function') sound.disconnect();
        });
        this.activeSounds = [];
    }
}

export default DuckHunt;